import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': { backgroundColor: theme.palette.error.dark },
  },
  warningText: { color: theme.palette.error.main, fontWeight: 600 },
  resourceList: { maxHeight: 160, overflowY: 'auto' },
}));

interface BoundResource {
  entityRef: string;
  name: string;
  title: string;
  subType: string;
  adoPipelineName: string;
  adoOrganization: string;
  adoProject: string;
  adoRepoName: string;
}

interface QueuedRun {
  runId: number;
  runUrl: string;
  organization: string;
  project: string;
  label: string;
}

type DestroyStatus = 'idle' | 'queuing' | 'waiting' | 'removing' | 'done' | 'error';

/**
 * Management card for azure-app Component entities.
 *
 * Flow:
 *  1. Queue destroy pipeline(s) — fire the ADO run(s)
 *  2. Poll all runs every 10 s until every run reaches `completed`
 *  3. If all succeeded → remove catalog entities and redirect
 *     If any failed/cancelled → surface the error, leave entity in catalog
 */
export function AzureDeleteAppCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const meta = entity.metadata as Record<string, any>;
  const appName = entity.metadata.name;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const pipelineName = meta.adoPipelineName as string | undefined;
  const dltPipelineName = meta.adoDltPipelineName as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;
  const entityRef = `component:${entity.metadata.namespace ?? 'default'}/${appName}`;

  const [boundResources, setBoundResources] = useState<BoundResource[]>([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<DestroyStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [queuedRuns, setQueuedRuns] = useState<QueuedRun[]>([]);
  const [pollStatus, setPollStatus] = useState('');

  // Load bound resources on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${baseUrl}/components/bound-resources?entityRef=${encodeURIComponent(entityRef)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok && !cancelled) {
          setBoundResources(await res.json());
        }
      } catch {
        // non-fatal
      } finally {
        if (!cancelled) setLoadingResources(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, identityApi, entityRef]);

  /** Queue a destroy run and return { runId, runUrl }. Returns null on failure (non-fatal). */
  const queueDestroyRun = async (
    baseUrl: string,
    token: string,
    org: string,
    proj: string,
    pipeline: string,
    label: string,
    targetEnvironment?: string,
    targetRepoName?: string,
  ): Promise<QueuedRun | null> => {
    try {
      const res = await fetch(`${baseUrl}/entities/destroy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
          organization: org,
          project: proj,
          pipelineName: pipeline,
          targetEnvironment,
          repoName: targetRepoName,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return { runId: data.runId, runUrl: data.runUrl, organization: org, project: proj, label };
    } catch {
      return null;
    }
  };

  /** Poll a single run until completed. Returns 'succeeded' | 'failed'. */
  const waitForRun = async (
    baseUrl: string,
    token: string,
    run: QueuedRun,
    onProgress: (msg: string) => void,
  ): Promise<'succeeded' | 'failed'> => {
    const POLL_INTERVAL_MS = 10_000;
    for (;;) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await fetch(
          `${baseUrl}/pipelines/run-status?organization=${encodeURIComponent(run.organization)}&project=${encodeURIComponent(run.project)}&runId=${run.runId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) continue;
        const data = await res.json();
        onProgress(`${run.label}: ${data.status}${data.result ? ` (${data.result})` : ''}`);
        if (data.status === 'completed') {
          return data.result === 'succeeded' ? 'succeeded' : 'failed';
        }
      } catch {
        // transient — keep polling
      }
    }
  };

  /** Remove catalog entities for the app and all bound resources. */
  const removeCatalogEntries = async () => {
    // Remove bound resources
    for (const resource of boundResources) {
      try {
        const resourceEntity = await catalogApi.getEntityByRef(resource.entityRef);
        if (resourceEntity) {
          const loc = resourceEntity.metadata.annotations?.['backstage.io/managed-by-location'];
          if (loc) {
            const location = await catalogApi.getLocationByRef(loc);
            if (location) await catalogApi.removeLocationById(location.id);
          }
          await catalogApi.removeEntityByUid(resourceEntity.metadata.uid!);
        }
      } catch {
        // best-effort
      }
    }

    // Remove the app entity
    const locationAnnotation = entity.metadata.annotations?.['backstage.io/managed-by-location'];
    if (locationAnnotation) {
      try {
        const location = await catalogApi.getLocationByRef(locationAnnotation);
        if (location) await catalogApi.removeLocationById(location.id);
      } catch {
        // best-effort
      }
    }
    await catalogApi.removeEntityByUid(entity.metadata.uid!);
  };

  const handleDelete = async () => {
    setStatus('queuing');
    setErrorMsg('');
    setPollStatus('');

    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token = '' } = await identityApi.getCredentials();

      // 1. Queue destroy runs for bound resources and the app itself
      const runs: QueuedRun[] = [];

      for (const resource of boundResources) {
        if (resource.adoPipelineName && resource.adoOrganization && resource.adoProject) {
          const run = await queueDestroyRun(
            baseUrl, token,
            resource.adoOrganization, resource.adoProject, resource.adoPipelineName,
            resource.title || resource.name,
          );
          if (run) runs.push(run);
        }
      }

      // Queue infra pipeline destroy
      if (organization && project && pipelineName) {
        const run = await queueDestroyRun(
          baseUrl, token, organization, project, pipelineName, `${appName} (infra)`,
        );
        if (run) runs.push(run);
      }
      // Queue DLT pipeline destroy for each deployed environment
      const envRefs: string[] = ((entity.spec as any)?.dependsOn ?? []).filter(
        (r: string) => r.toLowerCase().startsWith('azureenvironment:'),
      );
      if (organization && project && dltPipelineName && envRefs.length > 0) {
        for (const envRef of envRefs) {
          const envName = envRef.split('/').pop() ?? envRef;
          const run = await queueDestroyRun(
            baseUrl, token, organization, project, dltPipelineName, `${appName} DLT (${envName})`,
            envName,
            repoName,
          );
          if (run) runs.push(run);
        }
      }

      setQueuedRuns(runs);

      if (runs.length === 0) {
        // No pipelines to wait for — remove directly
        setStatus('removing');
        await removeCatalogEntries();
        setStatus('done');
        setTimeout(() => navigate('/catalog'), 3000);
        return;
      }

      // 2. Wait for all runs to complete
      setStatus('waiting');
      const results = await Promise.all(
        runs.map(run =>
          waitForRun(baseUrl, token, run, msg => setPollStatus(msg)),
        ),
      );

      const anyFailed = results.some(r => r === 'failed');
      if (anyFailed) {
        const failedLabels = runs
          .filter((_, i) => results[i] === 'failed')
          .map(r => r.label)
          .join(', ');
        throw new Error(
          `Destroy pipeline(s) failed: ${failedLabels}. The application has NOT been removed from the catalog. Check the pipeline run(s) for details.`,
        );
      }

      // 3. All pipelines succeeded — now remove from catalog
      setStatus('removing');
      await removeCatalogEntries();

      // Best-effort: delete ADO repos + pipeline definitions for the app and bound resources.
      // Errors here are non-fatal — the catalog entries are already gone.
      const cleanupRepo = async (org: string, proj: string, repo: string) => {
        try {
          await fetch(
            `${baseUrl}/ado/repo?entityRef=${encodeURIComponent(entityRef)}&organization=${encodeURIComponent(org)}&project=${encodeURIComponent(proj)}&repoName=${encodeURIComponent(repo)}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // best-effort
        }
      };

      // Clean up bound resource repos
      for (const resource of boundResources) {
        if (resource.adoOrganization && resource.adoProject && resource.adoRepoName) {
          await cleanupRepo(resource.adoOrganization, resource.adoProject, resource.adoRepoName);
        }
      }

      // Clean up the app repo (also deletes all pipeline definitions for the repo)
      if (organization && project && repoName) {
        await cleanupRepo(organization, project, repoName);
      }

      setStatus('done');
      setTimeout(() => navigate('/catalog'), 3000);
    } catch (e: any) {
      setStatus('error');
      setErrorMsg(e.message ?? 'Unknown error');
    }
  };

  const statusLabel: Record<DestroyStatus, string> = {
    idle: '',
    queuing: 'Queuing destroy pipeline(s)…',
    waiting: 'Waiting for pipeline(s) to complete…',
    removing: 'Removing catalog entries…',
    done: 'Done! Redirecting to catalog…',
    error: '',
  };

  return (
    <>
      <InfoCard title="Management" noPadding={false}>
        <Typography variant="body2" gutterBottom>
          Permanently destroy this application and all its bound resources, then remove everything
          from the catalog.
        </Typography>
        {loadingResources ? (
          <Progress />
        ) : boundResources.length > 0 ? (
          <>
            <Typography variant="body2" gutterBottom>
              <strong>{boundResources.length}</strong> bound resource(s) will also be destroyed:
            </Typography>
            <div className={classes.resourceList}>
              {boundResources.map(r => (
                <Chip
                  key={r.entityRef}
                  label={r.title || r.name}
                  size="small"
                  style={{ margin: 2 }}
                />
              ))}
            </div>
          </>
        ) : (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            No bound resources found.
          </Typography>
        )}
        <div style={{ marginTop: 16 }}>
          <Button
            variant="contained"
            className={classes.dangerButton}
            startIcon={<DeleteForeverIcon />}
            onClick={() => setOpen(true)}
            disabled={loadingResources || status === 'queuing' || status === 'waiting' || status === 'removing' || status === 'done'}
          >
            Delete Application
          </Button>
        </div>
      </InfoCard>

      <Dialog open={open} onClose={() => { if (status === 'idle' || status === 'error') setOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Application: {appName}</DialogTitle>
        <DialogContent>
          {status === 'idle' && (
            <>
              <DialogContentText>This will:</DialogContentText>
              <ul>
                {boundResources.length > 0 && (
                  <li>
                    Queue <strong>terraform destroy</strong> for {boundResources.length} bound resource(s)
                  </li>
                )}
                {pipelineName && (
                  <li>Queue <strong>destroy</strong> pipeline for the application</li>
                )}
                <li>Wait for all destroy pipelines to complete successfully</li>
                <li>Remove the application and all bound resources from the Backstage catalog</li>
                <li>Delete ADO repositories and pipeline definitions</li>
              </ul>
              <DialogContentText style={{ fontWeight: 600, marginTop: 8 }}>
                The following ADO repositories will be permanently deleted:
              </DialogContentText>
              <List dense className={classes.resourceList}>
                {repoName && (
                  <ListItem>
                    <ListItemText primary={repoName} secondary="application repo" />
                  </ListItem>
                )}
                {boundResources.filter(r => r.adoRepoName).map(r => (
                  <ListItem key={r.entityRef}>
                    <ListItemText primary={r.adoRepoName} secondary={r.title || r.name} />
                  </ListItem>
                ))}
              </List>
              <DialogContentText className={classes.warningText}>
                This action is irreversible. Type <strong>{appName}</strong> to confirm.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={appName}
                variant="outlined"
              />
            </>
          )}

          {(status === 'queuing' || status === 'waiting' || status === 'removing') && (
            <>
              <Progress />
              <DialogContentText style={{ marginTop: 16 }}>
                {statusLabel[status]}
              </DialogContentText>
              {queuedRuns.length > 0 && (
                <List dense>
                  {queuedRuns.map(r => (
                    <ListItem key={r.runId}>
                      <ListItemText
                        primary={r.label}
                        secondary={
                          <a href={r.runUrl} target="_blank" rel="noopener noreferrer">
                            View pipeline run →
                          </a>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {pollStatus && (
                <DialogContentText variant="caption" color="textSecondary">
                  {pollStatus}
                </DialogContentText>
              )}
            </>
          )}

          {status === 'done' && (
            <DialogContentText>
              All destroy pipelines completed successfully. Catalog entries removed. Redirecting…
            </DialogContentText>
          )}

          {status === 'error' && (
            <DialogContentText color="error">
              {errorMsg}
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => { setOpen(false); setConfirmText(''); setStatus('idle'); setErrorMsg(''); }}
            disabled={status === 'queuing' || status === 'waiting' || status === 'removing'}
          >
            {status === 'error' ? 'Close' : 'Cancel'}
          </Button>
          {status === 'idle' && (
            <Button
              className={classes.dangerButton}
              variant="contained"
              onClick={handleDelete}
              disabled={confirmText !== appName}
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
